/**
 * Price History Card component - mobile-friendly alternative to table
 * Displays a single price history entry in a card format optimized for small screens
 */

import { memo } from 'react'
import { formatVND } from '@/utils/currencyUtils'
import { formatLocalDate } from '@/utils/dateUtils'
import { Card } from '@/components/ui/card'
import type { GoldPriceHistory } from '@/types/price'

interface PriceHistoryCardProps {
  price: GoldPriceHistory
}

export const PriceHistoryCard = memo(({ price }: PriceHistoryCardProps) => {
  return (
    <Card className="p-4 w-full max-w-full overflow-hidden">
      {/* PRIORITY 1: Prices - Most Important */}
      <div className="space-y-3 mb-3">
        {/* Buy Price - Large and prominent */}
        <div className="flex justify-between items-baseline gap-2">
          <span className="text-sm text-muted-foreground shrink-0">Mua</span>
          <span className="text-xl font-bold text-destructive shrink-0">
            {formatVND(price.buyPrice)}
          </span>
        </div>
        
        {/* Sell Price - Large and prominent */}
        <div className="flex justify-between items-baseline gap-2">
          <span className="text-sm text-muted-foreground shrink-0">Bán</span>
          <span className="text-xl font-bold text-primary shrink-0">
            {formatVND(price.sellPrice)}
          </span>
        </div>
      </div>
      
      {/* PRIORITY 2: Date - Secondary Info */}
      <div className="pt-3 border-t border-border">
        <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
          <time className="shrink-0">{formatLocalDate(price.date)}</time>
          <span className="truncate max-w-[120px]">{price.branchName}</span>
        </div>
      </div>
    </Card>
  )
})

PriceHistoryCard.displayName = 'PriceHistoryCard'

